import {providerIsEnabled} from './provider-request.js';
import {pairIsAvailable} from './pair-availability.js?v=token-fix-20261009';

export function estimateFailure(status, body) {
  const detail = String(body?.error?.message || body?.error || body?.message || '').toLowerCase();
  if (status === 429) return 'Too many requests. Try shortly.';
  if (/lower than min|higher than max|minimum|maximum/.test(detail)) return 'Check the sending amount.';
  if (/no resulting quote|no quote/.test(detail) || status === 200) return 'No quote for this pair and amount.';
  return 'Quote service unavailable. Try again.';
}

export function bestReceiveAmount(data) {
  const quotes = data?.quotes?.quotes ?? data?.quotes;
  if (!Array.isArray(quotes)) return null;
  const amounts = quotes.filter(q => q && typeof q.provider === 'string' && q.provider.trim() && providerIsEnabled(q.provider))
    .map(q => Number(q.amount_to)).filter(n => Number.isFinite(n) && n > 0);
  return amounts.length ? Math.max(...amounts) : null;
}

function failure(status, body) {
  return Object.assign(new Error(estimateFailure(status, body)), {status, errorData: body});
}

// Reuse the displayed estimate briefly when the visitor clicks Compare providers.
// Never cache errors or confirmations, and never automatically retry a quote.
let lastQuote;
let limitedUntil = 0;
export async function requestQuote(input, {signal} = {}) {
  if (!pairIsAvailable(`${input.tickerFrom}_${input.networkFrom}`, `${input.tickerTo}_${input.networkTo}`)) throw failure(200, {});
  const params = new URLSearchParams({
    ticker_from: input.tickerFrom, network_from: input.networkFrom,
    ticker_to: input.tickerTo, network_to: input.networkTo,
    amount_from: String(Number(input.amountFrom)),
  });
  const key = params.toString();
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  if (lastQuote?.key === key && Date.now() - lastQuote.time < 15000) return lastQuote.body;
  if (Date.now() < limitedUntil) throw failure(429, {});
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal?.addEventListener('abort', cancel, {once: true});
  const timeout = setTimeout(cancel, 20000);
  try {
    const response = await fetch(`https://server.ghostswap.app/api/trocador/new_rate?${params}`, {
      signal: controller.signal, cache: 'no-store',
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      if (response.status === 429) {
        const retry = response.headers.get('Retry-After');
        const seconds = Number(retry);
        const delay = retry && Number.isFinite(seconds) ? seconds * 1000 : Date.parse(retry) - Date.now();
        limitedUntil = Date.now() + Math.max(30000, Number.isFinite(delay) ? delay : 0);
      }
      throw failure(response.status, body);
    }
    if (!body || typeof body !== 'object' || typeof body.trade_id !== 'string' || !body.trade_id.trim()) throw failure(502, {});
    if (bestReceiveAmount(body) === null) throw failure(200, body);
    lastQuote = {key, time: Date.now(), body};
    return body;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', cancel);
  }
}

export function quoteFailure(error) {
  if (error?.name === 'AbortError') return 'Quote timed out. Try again.';
  return error?.status ? estimateFailure(error.status, error.errorData) : 'Quote service unavailable. Try again.';
}
